/**
 * Провайдер бренда — docs/multi-brand.md §6.
 *
 * ЧТО ОН ДЕРЖИТ. Список брендов бэкенда (`GET /api/v1/brands`, один раз на сессию, уже
 * отсортирован бэкендом по `(ui.order, id)`), бренд из адреса (`/b/:brand/...`) и его вид —
 * тему, вордмарк, название. Тему он ставит на `<html>` атрибутом `data-theme` (у темы
 * `geoboost` атрибута нет: она умолчание `:root` в `styles/tokens.css`) и меняет иконку вкладки.
 *
 * ПОЧЕМУ БРЕНД ИЗ АДРЕСА, А НЕ ИЗ СОСТОЯНИЯ. Адрес — единственный источник правды о том, где
 * человек: ссылку на черновик пересылают, и она обязана открыться в теме своего бренда, а не
 * в теме того, что был открыт у получателя. Провайдер стоит НАД маршрутами (`App.tsx`), поэтому
 * параметров маршрута у него нет — бренд он читает из пути сам (`brandOfPath`).
 *
 * ЧТО ПОМНИТ БРАУЗЕР. Последний открытый бренд и его вид — `localStorage`, ключ
 * `smm.brand.last`. Им выбирается корень `/` и вид страницы входа (до входа список брендов
 * недоступен: ручка за сессией), и его же до первой отрисовки читает `public/theme-boot.js`,
 * чтобы тёмная тема не мигала светлой на каждой перезагрузке. Хранилище — удобство, а не
 * правда: каждое обращение в try/catch, и без него интерфейс работает (тема по адресу, корень —
 * первый бренд списка).
 *
 * ПОЧЕМУ СПИСОК РАЗБИРАЕТСЯ ПО ПОЛЯМ, А НЕ СТРОГОЙ СХЕМОЙ. Шапка с переключателем живёт на
 * каждом экране: чужой ответ (бэкенд без полей темы в окне выкладки) обязан давать бренды в
 * теме по умолчанию, а не пустой переключатель и белый экран. Незнакомая тема — тоже
 * умолчание: fail-safe в сторону вида, который уже есть.
 *
 * СЕРВИСЫ БРЕНДА (`agents`, docs/multi-brand.md §8). Бренд может жить одним интерфейсом —
 * без мониторинга и генерации. Его кнопки «Прогон мониторинга», «Запустить генерацию»,
 * «Переделать…» и перерисовка картинки остаются на своих местах, но гаснут с причиной
 * (`agentsOffReason`): заявку некому взять, и бэкенд её всё равно отобьёт.
 *
 * АВТООДОБРЕНИЕ (`auto_approval.allowed`, docs/ai-lab.md). Разрешение конфига бренда — замок 1
 * из 3 исключения из FR-4.1. Флаг здесь решает ровно одно: есть ли в шапке выключатель
 * «Автоодобрение» (`AutoApprovalSwitch`). Разбор в обратную сторону от сервисов: включает
 * выключатель только явное `true`, всё прочее — «нельзя». Сервис, погашенный по ошибке разбора,
 * стоит кнопки; выключатель, показанный по ошибке, стоит согласования человеком.
 *
 * ТРИ ВОЗМОЖНОСТИ БРЕНДА AGI BOARDROOM (2026-11-02) — те же поля ответа, разбор по тем же
 * правилам, и ни одного условия на id бренда в интерфейсе:
 *   · `author_opinion` — полоса «Мнение автора» под постом и потолок мнения (`brandAuthorOpinion`);
 *     как у автоодобрения, включает только явное `{allowed: true}`: полоса без включённой
 *     возможности дала бы кнопку, которую бэкенд отбивает 409;
 *   · `selectable_formats` — чипы формата «Запустить генерацию» (`brandFormats`); поля нет —
 *     прежний общий список, разбор — `selectableFormatsOf` словаря форматов;
 *   · `banned_orgs` — разметка организаций, запрещённых в РФ: предпросмотр редактора и гейт
 *     утверждения (`brandBannedOrgs`, `routes/bannedOrgs.ts`); только явное `true`. Это показ и
 *     раннее предупреждение, а не замок: разметку ставит бэкенд при сохранении, он же страхует
 *     одобрение, и ошибка разбора здесь стоит подсказки, а не поста.
 */

import {
  AUTHOR_OPINION_MAX_CODEPOINTS,
  BRAND_AGENTS_OFF_REASON,
  BRAND_THEMES,
  type BrandAgents,
  BrandAuthorOpinionSchema,
  type BrandTheme,
} from '@smm/contracts/schemas';
import {
  createContext,
  type JSX,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'wouter';
import { ApiRequestError } from './api/client.ts';
import { useResource } from './api/useResource.ts';
import {
  type OrderableFormat,
  SELECTABLE_FORMATS,
  selectableFormatsOf,
} from './components/formatLabel.ts';

/** Вид бренда в интерфейсе: чем рисуется знак и как бренд называется словами. */
export interface BrandLook {
  readonly theme: BrandTheme;
  /** Текст знака (`ui.wordmark` конфига; умолчание бэкенда — название бренда). */
  readonly wordmark: string;
  /** Название бренда для людей — `display_name` конфига. */
  readonly title: string;
}

/** «Добавить мнение» у бренда: потолок мнения, как его пишет человек, в кодпоинтах. */
export interface BrandAuthorOpinionEntry {
  readonly maxCodepoints: number;
}

/** Бренд из списка бэкенда — ровно то, что нужно интерфейсу для вида и для кнопок сервисов. */
export interface BrandEntry {
  readonly id: string;
  readonly title: string;
  readonly look: BrandLook;
  /** Какие сервисы обслуживают бренд (docs/multi-brand.md §8) — см. `brandAgents`. */
  readonly agents: BrandAgents;
  /** Конфиг бренда разрешает автоодобрение — см. `autoApprovalAllowed`. */
  readonly autoApprovalAllowed: boolean;
  /** «Добавить мнение» включено бренду; `null` — нет. См. `brandAuthorOpinion`. */
  readonly authorOpinion: BrandAuthorOpinionEntry | null;
  /** Что человек вправе заказать кнопкой «Запустить генерацию» — см. `brandFormats`. */
  readonly formats: readonly OrderableFormat[];
  /** Бренд размечает организации, запрещённые в РФ, — см. `brandBannedOrgs`. */
  readonly bannedOrgs: boolean;
}

/** Сервис, без которого действие интерфейса не будет обслужено. */
export type BrandAgent = keyof BrandAgents;

/** Оба сервиса подключены — так интерфейс вёл себя до флагов, и так ведёт, пока флагов не знает. */
export const AGENTS_ON: BrandAgents = { monitoring: true, generation: true };

/** Последний открытый бренд этого браузера. */
export interface RememberedBrand {
  readonly id: string;
  readonly look: BrandLook;
}

export interface BrandsState {
  /** Бренды в порядке бэкенда. `undefined` — список ещё едет, не загрузился или нет сессии. */
  readonly list: readonly BrandEntry[] | undefined;
  /** Почему списка нет, если он не загрузился или не разобрался; иначе `null`. */
  readonly error: string | null;
  readonly remembered: RememberedBrand | null;
  /** Вид, который провайдер поставил странице (тема на `<html>`). */
  readonly look: BrandLook;
}

export const DEFAULT_THEME: BrandTheme = 'geoboost';

/** Вид без единого сведения о бренде: страница входа без памяти, тесты отдельных экранов. */
export const DEFAULT_LOOK: BrandLook = {
  theme: DEFAULT_THEME,
  wordmark: 'GeoBoost',
  title: 'GeoBoost',
};

export const BRAND_STORAGE_KEY = 'smm.brand.last';

/** Форма id бренда — та же, что у конфига (`BRAND_RE` в контрактах). */
const BRAND_ID_RE = /^[a-z][a-z0-9_-]{1,31}$/;

const isTheme = (v: unknown): v is BrandTheme =>
  typeof v === 'string' && (BRAND_THEMES as readonly string[]).includes(v);

const nonEmpty = (v: unknown, max: number): v is string =>
  typeof v === 'string' && v.length > 0 && v.length <= max;

/**
 * Разрешение автоодобрения из ответа: только `{allowed: true}`. Обратное правилу сервисов ниже —
 * см. шапку файла: незнакомый ответ выключателя не показывает.
 */
function autoApprovalOf(v: unknown): boolean {
  return typeof v === 'object' && v !== null && (v as Record<string, unknown>)['allowed'] === true;
}

/**
 * Флаги сервисов из ответа. Гасит только явное `false`: всё остальное (поля нет, флаг не
 * булев) — «не знаю», и интерфейс ведёт себя как до флагов. Замок не здесь, а в бэкенде
 * (`assertBrandAgents`): заявку бренда без сервиса он отобьёт словами при любой кнопке, а
 * погашенная по ошибке разбора кнопка у geoboost отняла бы генерацию без всякой причины.
 */
function agentsOf(v: unknown): BrandAgents {
  if (typeof v !== 'object' || v === null) return AGENTS_ON;
  const a = v as Record<string, unknown>;
  return { monitoring: a['monitoring'] !== false, generation: a['generation'] !== false };
}

/**
 * «Добавить мнение» из ответа — по правилу автоодобрения: полосу включает только явное
 * `{allowed: true}`. Потолок — свой у бренда, если он годен схемой контрактов, иначе общий
 * (`AUTHOR_OPINION_MAX_CODEPOINTS`): незнакомое число не должно ни поднять потолок выше того, что
 * примет база, ни закрыть полосу, которую бэкенд разрешил.
 */
function authorOpinionOf(v: unknown): BrandAuthorOpinionEntry | null {
  if (typeof v !== 'object' || v === null) return null;
  const o = v as Record<string, unknown>;
  if (o['allowed'] !== true) return null;
  const max = BrandAuthorOpinionSchema.shape.max_codepoints.safeParse(o['max_codepoints']);
  return {
    maxCodepoints: max.success && max.data !== undefined ? max.data : AUTHOR_OPINION_MAX_CODEPOINTS,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Разбор ответа и памяти

/**
 * Список брендов из ответа бэкенда. `null` — ответ не похож на список. Элемент без `id` или с
 * чужим `id` пропускается: переключатель, ведущий на несуществующий адрес, хуже пропавшей
 * строки, а остальные бренды от одного битого элемента не страдают.
 */
export function brandEntriesOf(data: unknown): readonly BrandEntry[] | null {
  if (!Array.isArray(data)) return null;
  const out: BrandEntry[] = [];
  for (const raw of data as readonly unknown[]) {
    if (typeof raw !== 'object' || raw === null) continue;
    const b = raw as Record<string, unknown>;
    const id = b['id'];
    if (typeof id !== 'string' || !BRAND_ID_RE.test(id)) continue;
    const title = nonEmpty(b['title'], 200) ? b['title'] : id;
    const theme = isTheme(b['theme']) ? b['theme'] : DEFAULT_THEME;
    const wordmark = nonEmpty(b['wordmark'], 64) ? b['wordmark'] : title;
    out.push({
      id,
      title,
      look: { theme, wordmark, title },
      agents: agentsOf(b['agents']),
      autoApprovalAllowed: autoApprovalOf(b['auto_approval']),
      authorOpinion: authorOpinionOf(b['author_opinion']),
      formats: selectableFormatsOf(b['selectable_formats']),
      bannedOrgs: b['banned_orgs'] === true,
    });
  }
  return out;
}

function rememberedOf(v: unknown): RememberedBrand | null {
  if (typeof v !== 'object' || v === null) return null;
  const r = v as Record<string, unknown>;
  const id = r['id'];
  const look = r['look'];
  if (typeof id !== 'string' || !BRAND_ID_RE.test(id)) return null;
  if (typeof look !== 'object' || look === null) return null;
  const l = look as Record<string, unknown>;
  const theme = l['theme'];
  const wordmark = l['wordmark'];
  const title = l['title'];
  if (!isTheme(theme) || !nonEmpty(wordmark, 64) || !nonEmpty(title, 200)) return null;
  return { id, look: { theme, wordmark, title } };
}

/** Память браузера. Недоступное или испорченное хранилище — «не помню», а не исключение. */
export function readRememberedBrand(): RememberedBrand | null {
  try {
    const raw = window.localStorage.getItem(BRAND_STORAGE_KEY);
    return raw === null ? null : rememberedOf(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeRememberedBrand(r: RememberedBrand): void {
  try {
    window.localStorage.setItem(BRAND_STORAGE_KEY, JSON.stringify(r));
  } catch {
    /* приватное окно или запрет хранилища: помнить нечем, работает и так */
  }
}

/** Бренд из пути `/b/:brand/...`; вне этих адресов — `null`. */
export function brandOfPath(path: string): string | null {
  const m = /^\/b\/([^/]+)/.exec(path);
  if (m === null || m[1] === undefined) return null;
  let id: string;
  try {
    id = decodeURIComponent(m[1]);
  } catch {
    return null;
  }
  return BRAND_ID_RE.test(id) ? id : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Вид бренда по id

/**
 * Вид бренда: из списка, если он пришёл; пока не пришёл — из памяти, если она про этот же
 * бренд (иначе каждая перезагрузка страницы второго бренда мигала бы видом первого); иначе —
 * умолчание. Название при этом не выдумывается: без списка и памяти это id бренда.
 */
export function brandLook(state: BrandsState, id: string): BrandLook {
  const found = state.list?.find((b) => b.id === id);
  if (found !== undefined) return found.look;
  if (state.remembered?.id === id) return state.remembered.look;
  return { ...DEFAULT_LOOK, title: id };
}

/** Название бренда для текста («режим AGI Boardroom»). */
export const brandTitle = (state: BrandsState, id: string): string => brandLook(state, id).title;

/** Название бренда текстом — для подзаголовков экранов («AGI Boardroom · сетка слотов…»). */
export function BrandTitle({ id }: { id: string }): JSX.Element {
  return <>{brandTitle(useBrands(), id)}</>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Сервисы бренда (docs/multi-brand.md §8)

/**
 * Какие сервисы обслуживают бренд. Пока список едет, не пришёл или бренда в нём нет — оба
 * подключены: так было до флагов, и geoboost не мигает погашенными кнопками на каждой
 * загрузке. Заявку бренда без сервиса в это окно отобьёт бэкенд, назвав причину.
 */
export function brandAgents(state: BrandsState, id: string): BrandAgents {
  return state.list?.find((b) => b.id === id)?.agents ?? AGENTS_ON;
}

/**
 * Почему действие недоступно — словами, с маленькой буквы (встаёт после имени действия:
 * «Запустить генерацию — генерация для AGI Boardroom ещё не подключена»). `null` — все
 * нужные действию сервисы у бренда есть. Смысл тот же, что у отказа бэкенда
 * (`assertBrandAgents`), чтобы погашенная кнопка и отказ не спорили друг с другом.
 */
export function agentsOffReason(
  state: BrandsState,
  id: string,
  need: readonly BrandAgent[],
): string | null {
  const agents = brandAgents(state, id);
  const missing = need.filter((a) => !agents[a]);
  if (missing.length === 0) return null;
  const title = brandTitle(state, id);
  if (missing.length > 1) return `мониторинг и генерация для ${title} ещё не подключены`;
  return missing[0] === 'monitoring'
    ? `мониторинг для ${title} ещё не подключён`
    : `генерация для ${title} ещё не подключена`;
}

/**
 * Разрешает ли конфиг бренда автоодобрение (замок 1 из 3, docs/ai-lab.md). Пока список едет, не
 * пришёл или бренда в нём нет — нет: выключатель появляется, только когда бэкенд это подтвердил.
 */
export function autoApprovalAllowed(state: BrandsState, id: string): boolean {
  return state.list?.find((b) => b.id === id)?.autoApprovalAllowed === true;
}

/**
 * «Добавить мнение» у бренда (AGI Boardroom, 2026-11-02): потолок мнения или `null` — полосы
 * мнения нет. Пока список едет, не пришёл или бренда в нём нет — `null`, как у автоодобрения:
 * полоса появляется, только когда бэкенд подтвердил возможность.
 */
export function brandAuthorOpinion(state: BrandsState, id: string): BrandAuthorOpinionEntry | null {
  return state.list?.find((b) => b.id === id)?.authorOpinion ?? null;
}

/**
 * Чипы формата «Запустить генерацию» у бренда. Пока список едет, не пришёл или бренда в нём нет —
 * прежний общий список: так диалог вёл себя до поля, и кнопку, которой бренду не положено,
 * бэкенд отобьёт словами (формат вне базы знаний бренда — 422).
 */
export function brandFormats(state: BrandsState, id: string): readonly OrderableFormat[] {
  return state.list?.find((b) => b.id === id)?.formats ?? SELECTABLE_FORMATS;
}

/**
 * Размечает ли бренд организации, запрещённые в РФ. Пока список едет — нет: предпросмотр
 * редактора показывает набранное как есть и гейта утверждения нет, а звёздочки и сноску всё
 * равно поставит бэкенд, и одобрение неразмеченного он отобьёт сам.
 */
export function brandBannedOrgs(state: BrandsState, id: string): boolean {
  return state.list?.find((b) => b.id === id)?.bannedOrgs === true;
}

/** Имя погашенного действия вместе с причиной — для `aria-label` и `title` разом. */
export const offLabel = (name: string, reason: string): string => `${name} — ${reason}`;

/**
 * Отказ бэкенда «у бренда нет нужного сервиса». Диалог, открытый до того, как список брендов
 * погасил кнопку, получит его вместо обычного ответа — и обязан показать текст сервера, а не
 * своё прочтение 409 («черновик изменился»).
 */
export function isAgentsOff(e: unknown): e is ApiRequestError {
  if (!(e instanceof ApiRequestError)) return false;
  const d = e.details;
  return (
    typeof d === 'object' &&
    d !== null &&
    (d as Record<string, unknown>)['reason'] === BRAND_AGENTS_OFF_REASON
  );
}

/**
 * Куда вести с корня `/`: последний открытый бренд, если он ещё в списке, иначе первый.
 * `null` — список пуст (ни один конфиг бренда не загрузился на бэкенде).
 */
export function rootBrand(state: BrandsState): string | null {
  const list = state.list ?? [];
  const last = state.remembered?.id;
  if (last !== undefined && list.some((b) => b.id === last)) return last;
  return list[0]?.id ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Тема страницы и иконка вкладки

/**
 * Иконка вкладки по теме. `Record` по `BrandTheme`, а не словарь со строками: новая тема в
 * контрактах без своей иконки — ошибка компиляции здесь, а не вкладка с чужим знаком.
 * Файлы лежат в `public/` (docs/frontend.md §«Иконка вкладки»); PNG — для Safari.
 */
const FAVICONS: Readonly<Record<BrandTheme, { readonly svg: string; readonly png: string }>> = {
  geoboost: { svg: '/favicon.svg', png: '/favicon-32.png' },
  strategai: { svg: '/favicon-strategai.svg', png: '/favicon-strategai-32.png' },
  'strategai-light': {
    svg: '/favicon-strategai-light.svg',
    png: '/favicon-strategai-light-32.png',
  },
};

function applyTheme(theme: BrandTheme): void {
  const root = document.documentElement;
  if (theme === DEFAULT_THEME) root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  const icons = FAVICONS[theme];
  for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')) {
    const href = link.type === 'image/png' ? icons.png : icons.svg;
    // Тот же адрес не переписывается: браузер перезапросил бы иконку на каждом переходе.
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Контекст

/** Вне провайдера (тесты отдельных экранов) — ничего не известно и тема по умолчанию. */
const DETACHED: BrandsState = {
  list: undefined,
  error: null,
  remembered: null,
  look: DEFAULT_LOOK,
};

const BrandsContext = createContext<BrandsState>(DETACHED);

export function useBrands(): BrandsState {
  return useContext(BrandsContext);
}

export function BrandProvider({
  enabled,
  children,
}: {
  /** Грузить ли список: ручка брендов за сессией, до входа её не спрашивают. */
  enabled: boolean;
  children: ReactNode;
}): JSX.Element {
  const res = useResource<unknown>(enabled ? '/api/v1/brands' : null);
  const [location] = useLocation();
  const [remembered, setRemembered] = useState<RememberedBrand | null>(readRememberedBrand);

  const parsed = useMemo(
    () => (res.data === undefined ? undefined : brandEntriesOf(res.data)),
    [res.data],
  );
  const list = parsed ?? undefined;
  const error =
    res.error !== undefined
      ? res.error.message
      : parsed === null
        ? 'ответ бэкенда — не список брендов'
        : null;

  const urlBrand = brandOfPath(location);
  const base = useMemo<BrandsState>(
    () => ({ list, error, remembered, look: DEFAULT_LOOK }),
    [list, error, remembered],
  );
  // Вне `/b/:brand` (вход, корень, «такого экрана нет») — вид последнего бренда.
  const look = useMemo(
    () => (urlBrand === null ? (base.remembered?.look ?? DEFAULT_LOOK) : brandLook(base, urlBrand)),
    [base, urlBrand],
  );

  // До отрисовки кадра: иначе экран бренда мелькнул бы в теме предыдущего.
  useLayoutEffect(() => {
    applyTheme(look.theme);
  }, [look.theme]);
  // Сброс — только когда провайдера не стало (тесты, размонтирование), а не между темами:
  // уборка на каждой смене дёргала бы иконку вкладки туда и обратно.
  useLayoutEffect(
    () => () => {
      applyTheme(DEFAULT_THEME);
    },
    [],
  );

  // Помним бренд, только когда он подтверждён списком бэкенда: опечатка в адресе или бренд,
  // чей конфиг не загрузился, не должны становиться корнем `/` у этого браузера.
  const active = urlBrand === null ? undefined : list?.find((b) => b.id === urlBrand);
  useEffect(() => {
    if (active === undefined) return;
    if (remembered !== null && sameRemembered(remembered, active)) return;
    const next: RememberedBrand = { id: active.id, look: active.look };
    writeRememberedBrand(next);
    setRemembered(next);
  }, [active, remembered]);

  const value = useMemo<BrandsState>(() => ({ ...base, look }), [base, look]);
  return <BrandsContext.Provider value={value}>{children}</BrandsContext.Provider>;
}

const sameRemembered = (r: RememberedBrand, b: BrandEntry): boolean =>
  r.id === b.id &&
  r.look.theme === b.look.theme &&
  r.look.wordmark === b.look.wordmark &&
  r.look.title === b.look.title;
